import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import { operators as o } from '@pendo/aggregations';
import { getIntegration, saveJoinKeys } from '@/integrations/sf/sync.utils';

export const SALESFORCE_PUSH_FLAG = 'salesforce-push';

export function SFReportModifier (segmentId, columns, kind) {
    return function (agg) {
        const aggColumnRegex = /daysActive|eventCount|eventTime|numVisitors/i;
        agg.pipeline = flatMap(agg.pipeline, (operator) => {
            if (operator.source) {
                // Always use visitor source to support segments that might include visitorId
                const sourceAggList = [
                    o.sources.visitors(),
                    // always unwind accounts for segments that might contain account rules
                    o.evaluate('accountId', 'metadata.auto.accountids'),
                    kind === 'account' && o.filter('accountId != null'),
                    o.unwind('accountId', { keepEmpty: true }),
                ].filter(Boolean);

                // base agg source around including all accounts or visitors
                return sourceAggList;
            }

            if (operator.identified) {
                return o.identified('visitorId');
            }

            if (operator.select) {
                // modify selects to request 0s instead of nulls for calculated values.
                const { select } = operator;
                Object.keys(select).forEach((key) => {
                    if (aggColumnRegex.test(key)) {
                        select[key] = `if(isNull(${key}), 0, ${key})`;
                    }
                });

                return {
                    select,
                };
            }

            return operator;
        });

        return agg;
    };
}

export async function updateReportMapping (report) {
    if (hasFlag(report, SALESFORCE_PUSH_FLAG)) {
        const configuration = await getIntegration();

        if (configuration?.data?.reports && configuration.data.reports[report.id]) {
            const reportConfig = configuration.data.reports[report.id];
            const mappedColumnIds = Object.keys(reportConfig.fields);
            const reportColumnIds = map(report.definition.config.columns, 'columnName');
            const needToUnmap = difference(mappedColumnIds, reportColumnIds);

            if (needToUnmap.length) {
                // Remove field(s) from SF push configuration
                needToUnmap.forEach((columnId) => {
                    delete reportConfig.fields[columnId];
                });
                await saveJoinKeys(configuration, configuration.data);
            }
        }

        return report;
    }

    return report;
}

export async function removeReportMapping (report) {
    if (hasFlag(report, SALESFORCE_PUSH_FLAG)) {
        const configuration = await getIntegration();

        if (configuration?.data?.reports && configuration.data.reports[report.id]) {
            // Remove report from SF push configuration
            delete configuration.data.reports[report.id];
            await saveJoinKeys(configuration, configuration.data);
        }

        return report;
    }

    return report;
}

export function addFlag (report, flag) {
    report.flags = report.flags || [];

    if (!hasFlag(report, flag)) {
        report.flags.push(flag);
    }

    return report;
}

export function removeFlag (report, flag) {
    report.flags = report.flags || [];

    const index = report.flags.indexOf(flag);
    if (index >= 0) {
        report.flags.splice(index, 1);
    }

    return report;
}

export function hasFlag (report, flag) {
    if (!report || !report.flags) return false;

    return report.flags.includes(flag);
}

export function updateReportForSalesforcePush (report) {
    const updatedReport = cloneDeep(report);

    // We unconditionally apply these every time for some reason
    updatedReport.definition.config.everyone = true; // include all visitors/accounts
    addFlag(updatedReport, SALESFORCE_PUSH_FLAG);

    return updatedReport;
}
